import * as React from 'react';
import { Toggler } from '../../Toggler/toggler';
import { SchemaField } from '../../../types/Metadata';
import { Group } from 'rcd-cpp-ui/ui/form/Group';
import './fieldCard.scss';
import { Typeahead } from '../../Typeahead/typeahead';
import { FieldType } from '../../../enums/FieldType';
import { DataType } from '../../../enums/DataType';
import { FieldIcon, SixDots } from '../../Icon/icon';
import { useActions } from '../schemaModalActions';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { NodeType } from '../../../enums/NodeType';
import {
  audienceDetailsFields,
  dndCardDetails,
  getSchema,
  isAuditType
} from '../../../utils';
import { WYSIWYGEditor } from '../../WYSIWYGEditor/WYSIWYGEditor';
import { FieldRequiredValue } from '../../../enums/FieldRequiredValue';
import { FieldShowValue } from '../../../enums/FieldShowValue';
import { PermissionField } from './permissionField';
import { useSelector } from '../../../utils/useSelector';

interface Props {
  nodeType: NodeType;
  field: SchemaField;
  orderField: SchemaField;
  displayField: SchemaField;
  disabled: boolean;
  onClick: () => void;
  onMove: (index1: number, index2: number) => void;
  index: number;
  disableType: boolean;
  dataTypes: { dataType: NodeType | DataType; name: string }[];
  existingField: boolean;
}

type FieldTypeOption = {
  fieldType?: FieldType;
  dataType?: DataType;
  name: string;
  Icon: any;
};

export const FieldTypeOptions: FieldTypeOption[] = [
  {
    dataType: DataType.string,
    name: 'Text',
    Icon: FieldIcon.Text,
    fieldType: FieldType.static
  },
  {
    dataType: DataType.number,
    name: 'Numeric',
    Icon: FieldIcon.Numeric,
    fieldType: FieldType.static
  },
  {
    dataType: DataType.date,
    name: 'Date',
    Icon: FieldIcon.Date,
    fieldType: FieldType.static
  },
  {
    dataType: DataType.boolean,
    name: 'True/False',
    Icon: FieldIcon.Boolean,
    fieldType: FieldType.static
  },
  {
    fieldType: FieldType.reference,
    name: 'Reference',
    Icon: FieldIcon.Reference
  },
  {
    dataType: DataType.longString,
    name: 'Text Area',
    Icon: FieldIcon.Text,
    fieldType: FieldType.static
  },
  {
    fieldType: FieldType.custom,
    dataType: DataType.label,
    name: 'Label',
    Icon: FieldIcon.Text
  }
];

export const StringTypeOpions: FieldTypeOption[] = [
  {
    dataType: DataType.string,
    name: 'Text',
    Icon: FieldIcon.Text,
    fieldType: FieldType.static
  },
  {
    dataType: DataType.longString,
    name: 'Text Area',
    Icon: FieldIcon.Text,
    fieldType: FieldType.static
  }
];

export const FieldCard = (p: Props) => {
  const schema = useSelector(s =>
    getSchema(s.planning?.selectedWorkspace?.schemas, s.schemaModal?.nodeType)
  );
  const {
    onMove,
    index,
    onClick,
    disableType,
    nodeType,
    dataTypes,
    orderField,
    existingField,
    displayField
  } = p;
  const {
    fieldType,
    dataType,
    relationType,
    field,
    name,
    requiredAt,
    description,
    multiValue
  } = p.field;
  const { updateField } = useActions();
  const fieldTypeName = FieldTypeOptions.filter(f => {
    if (f.fieldType == FieldType.reference) return fieldType == f.fieldType;
    return f.dataType == dataType;
  })[0]?.name;
  const selectedDataTypeName = findOne(dataTypes, { dataType })?.name;

  const { ref, opacity, handlerId } = dndCardDetails(onMove, field, index);

  const isOrderField = orderField
    ? orderField.field == field
    : displayField?.field == field;

  const requiredOptions = RequiredOptions;
  const requiredValue = findOne(requiredOptions, { value: requiredAt });

  return (
    <div
      className="card"
      onClick={onClick}
      ref={ref}
      style={{ opacity }}
      data-handler-id={handlerId}
    >
      <div className="field-col">{name || '--'}</div>
      <div className="field-col">{fieldTypeName || '--'}</div>
      <div className="field-col">{requiredValue?.label}</div>
      <div className="field-col"></div>
      <div className="field-form ">
        <Group className="name" label="Field Name">
          <input
            type="text"
            className="form-control"
            disabled={p.disabled}
            value={name || ''}
            onChange={e => updateField(field, { name: e.target.value })}
          />
        </Group>
        {(([FieldType.reference, FieldType.static].includes(fieldType) &&
          !isAuditType(relationType)) ||
          DataType.dataSourceProjects == p?.field?.dataType) && (
          <Group label="Required to Enter">
            <Selection
              value={requiredAt}
              disabled={isRequireDisabled(p.field, p.disabled, nodeType)}
              onChange={(e: any) => {
                const v = e.target.value;
                updateField(field, { requiredAt: v });
              }}
              options={requiredOptions}
            />
          </Group>
        )}
        <Group label="Field Type">
          <Typeahead
            value={fieldTypeName}
            options={existingField ? StringTypeOpions : FieldTypeOptions}
            disabled={p.disabled || disableType}
            showAllByDefault
            includes={() => true}
            onSelect={o => {
              updateField(field, {
                fieldType: o.fieldType,
                dataType: o.dataType,
                requiredAt: FieldRequiredValue.NotRequired,
                multiValue: undefined
              });
            }}
            renderOption={o => {
              return (
                <span className="field-type-option">
                  <o.Icon />
                  {o.name}
                </span>
              );
            }}
          />
        </Group>
        {fieldType == FieldType.reference && (
          <>
            <Group label="Relation Type">
              <Selection
                value={relationType}
                disabled={p.disabled || disableType}
                onChange={(e: any) => {
                  const v = e.target.value;
                  const multiValue =
                    v == 'CHILD_OF'
                      ? false
                      : v == 'PARENT_OF'
                        ? true
                        : undefined;
                  if (!v) {
                    updateField(field, { relationType: null, multiValue });
                  } else {
                    updateField(field, { relationType: v, multiValue });
                  }
                }}
                options={getRelationTypes(p.nodeType, relationType)}
              />
            </Group>
            <Group label="Data Type">
              <Typeahead
                value={selectedDataTypeName}
                options={dataTypes.filter(
                  dt => !(dt.dataType == NodeType.Item)
                )}
                disabled={p.disabled || disableType}
                showAllByDefault
                onSelect={o =>
                  updateField(field, {
                    dataType: o.dataType
                  })
                }
                renderOption={o => o.name}
              />
            </Group>
          </>
        )}
        <PermissionField
          schema={schema}
          f={p.field}
          onChange={editableCriteria =>
            updateField(field, { editableCriteria })
          }
        />
        <Group label="Description" className="schema-field-description">
          <WYSIWYGEditor
            content={description ? description : p.disabled ? '--' : ''}
            disabled={p.disabled}
            readonly={p.disabled}
            onChange={e => updateField(field, { description: e })}
          />
        </Group>
        <Group className="field-without-label">
          <div className="field-togglers">
            {fieldType == FieldType.reference && (
              <Toggler
                label="Allow Multiple Values"
                disabled={
                  p.disabled ||
                  relationType == 'PARENT_OF' ||
                  relationType == 'CHILD_OF' ||
                  relationType == 'EQUIVALENT'
                }
                checked={multiValue}
                onChange={v => updateField(field, { multiValue: v })}
              />
            )}
          </div>
          {(!fieldType || fieldType == FieldType.static) && (
            <Toggler
              styles={{ marginTop: '10px' }}
              label="Display Order"
              checked={isOrderField}
              onChange={v => updateField(field, { isOrderField: v })}
            />
          )}
        </Group>
      </div>

      <div className="field-card-dots">
        <SixDots />
      </div>
    </div>
  );
};

export const isShowDisabled = (sf: SchemaField) =>
  ['Item.title', 'Item.status', 'Item.configureWorkflowType'].includes(
    sf.key
  ) || audienceDetailsFields.includes(sf.field);

export const isRequireDisabled = (
  sf: SchemaField,
  disabled: boolean,
  nodeType: NodeType
) => {
  const { fieldType, field, key, dataType, readOnly } = sf;
  return (
    fieldType == FieldType.computed ||
    ['createdBy', 'modifiedBy', 'configureWorkflowType']
      .concat(audienceDetailsFields)
      .includes(field) ||
    ['Item.itemId', 'Item.status', 'Item.title', 'Item.uniBy'].includes(key) ||
    (nodeType != NodeType.Item && disabled) ||
    dataType == DataType.boolean ||
    readOnly == true
  );
};

export type SelectionProps = {
  options: { value: string; label: string }[];
} & Pick<React.HTMLProps<HTMLSelectElement>, 'onChange' | 'value' | 'disabled'>;

export const Selection = ({
  value,
  options,
  disabled,
  onChange
}: SelectionProps) => {
  return (
    <select
      className="form-control"
      value={value}
      disabled={disabled}
      onChange={onChange}
    >
      {options.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  );
};

export const getRelationTypes = (nodeType: NodeType, relationType: string) => {
  const list = [
    { value: '', label: 'Associated With' },
    { value: 'EQUIVALENT', label: 'Equivalent' },
    { value: 'IMPLIES', label: 'Implies' },
    { value: 'IMPLIED_BY', label: 'Implied By' },
    { value: 'CHILD_OF', label: 'Child Of' },
    { value: 'PARENT_OF', label: 'Parent Of' }
  ];
  if (nodeType == NodeType.Item) {
    return list.filter(o => o.value == relationType || !o.value);
  }
  return list;
};

export const ItemRequiredOptions = [
  { value: FieldRequiredValue.NotRequired, label: 'Not required' },
  { value: FieldRequiredValue.Idea, label: 'Idea' },
  { value: FieldRequiredValue.ConceptReview, label: 'Concept Review' },
  {
    value: FieldRequiredValue.ReviewDone,
    label: 'Review Done: Start Production'
  }
];
export const RequiredOptions = [
  { value: FieldRequiredValue.NotRequired, label: 'Not required' },
  { value: FieldRequiredValue.Required, label: 'Required' }
];
export const ItemShowOptions = [
  { value: FieldShowValue.IdeaCreation, label: 'Idea creation' },
  { value: FieldShowValue.Idea, label: 'Idea' },
  { value: FieldShowValue.ConceptReview, label: 'Concept Review' },
  { value: FieldShowValue.Hide, label: 'Not Show' },
  { value: FieldShowValue.AlwaysHide, label: 'Never Show' }
];

export const getShowOptions = (requiredAt: FieldRequiredValue) => {
  switch (requiredAt) {
    case FieldRequiredValue.Idea:
      return ItemShowOptions.slice(0, 1);
    case FieldRequiredValue.ConceptReview:
      return ItemShowOptions.slice(0, 2);
    case FieldRequiredValue.ReviewDone:
      return ItemShowOptions.slice(0, 3);
  }
  return ItemShowOptions;
};
